import React, { useState, useEffect } from 'react';
import {
  Save,
  Eye,
  EyeOff,
  Shield,
  AlertCircle,
  Check,
  X,
  LayoutDashboard,
  Users,
  DollarSign,
  Calendar,
  Music,
  FileText,
  Cake,
  Settings as SettingsIcon,
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { hasPermission } from '../../utils/helpers';
import { ROLES } from '../../utils/constants';
import { logger } from '../../utils/logger';

const MODULES = [
  {
    id: 'dashboard',
    name: 'Tableau de bord',
    icon: LayoutDashboard,
    description: 'Vue d’ensemble des informations clés',
    permission: null, // accessible si connecté
  },
  {
    id: 'programmes',
    name: 'Programmes',
    icon: Calendar,
    description: 'Gestion des cultes et programmes',
    permission: 'view_programmes',
  },
  {
    id: 'fihirana',
    name: 'Fihirana',
    icon: Music,
    description: 'Gestion des cantiques',
    permission: 'view_fihirana',
  },
  {
    id: 'birthdays',
    name: 'Anniversaires',
    icon: Cake,
    description: 'Liste des anniversaires des membres',
    permission: null, // accessible à tous les membres
  },
  {
    id: 'journal',
    name: 'Journal',
    icon: FileText,
    description: 'Journal de l’église et comptes-rendus',
    permission: 'view_programmes',
  },
  {
    id: 'transactions',
    name: 'Transactions',
    icon: DollarSign,
    description: 'Gestion financière (revenus et dépenses)',
    permission: 'view_finances',
  },
  {
    id: 'members',
    name: 'Membres',
    icon: Users,
    description: "Gestion des membres de l'église",
    permission: 'view_members',
  },
  {
    id: 'reports',
    name: 'Rapports',
    icon: FileText,
    description: 'Rapports financiers et exports',
    permission: 'export_all',
  },
  {
    id: 'settings',
    name: 'Paramètres',
    icon: SettingsIcon,
    description: 'Configuration de l’application',
    permission: null, // généralement réservé aux admins
  },
];

const ModulePermissionsManager = () => {
  const [permissions, setPermissions] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const roleIds = Object.keys(ROLES || {});

  // 🔄 Charger depuis Firestore au montage + hydrater cache & localStorage
  useEffect(() => {
    const loadPermissions = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const ref = doc(db, 'settings', 'modulePermissions');
        const snap = await getDoc(ref);

        let loaded = {};
        if (snap.exists()) {
          loaded = snap.data(); // { roleId: { moduleId: boolean } }
          logger.log('✅ Permissions modules chargées depuis Firestore:', loaded);
        } else {
          logger.log('ℹ️ Aucun document modulePermissions, initialisation vide');
        }

        setPermissions(loaded);

        // 🧠 Cache global utilisé par hasModuleAccess
        try {
          window.__modulePermissionsCache = loaded;
        } catch (e) {
          logger.error('Erreur lors de la mise à jour du cache global modulePermissions:', e);
        }

        // 🧠 Persistance localStorage (fallback)
        try {
          localStorage.setItem('modulePermissions', JSON.stringify(loaded));
        } catch (e) {
          logger.error('Erreur en écrivant modulePermissions dans localStorage:', e);
        }

        setHasChanges(false);
      } catch (error) {
        logger.error('❌ Erreur chargement permissions modules:', error);
        setLoadError("Impossible de charger les permissions des modules.");
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, []);

  // Baseline: par défaut, est-ce qu’un rôle a *théoriquement* accès à un module ?
  const getDefaultAccess = (roleId, moduleId) => {
    const role = ROLES?.[roleId];
    if (!role) return false;

    const moduleDef = MODULES.find((m) => m.id === moduleId);
    if (!moduleDef) return false;

    // Si le module n’a pas de permission associée → accessible par défaut
    if (!moduleDef.permission) return true;

    return role.permissions?.includes(moduleDef.permission);
  };

  // Lis la valeur effective (override > sinon valeur par défaut)
  const getEffectiveAccess = (roleId, moduleId) => {
    const rolePerms = permissions[roleId] || {};
    if (Object.prototype.hasOwnProperty.call(rolePerms, moduleId)) {
      return !!rolePerms[moduleId];
    }
    return getDefaultAccess(roleId, moduleId);
  };

  const togglePermission = (roleId, moduleId) => {
    setPermissions((prev) => {
      const currentRole = prev[roleId] || {};
      const currentValue = getEffectiveAccess(roleId, moduleId);
      const newRolePerms = {
        ...currentRole,
        [moduleId]: !currentValue,
      };

      const updated = {
        ...prev,
        [roleId]: newRolePerms,
      };

      return updated;
    });
    setHasChanges(true);
  };

  const resetRole = (roleId) => {
    setPermissions((prev) => {
      const updated = { ...prev };
      delete updated[roleId];
      return updated;
    });
    setHasChanges(true);
  };

  const resetAll = () => {
    setPermissions({});
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoadError('');
      logger.log('💾 Sauvegarde des permissions modules...', permissions);

      // 1️⃣ Sauvegarde Cloud (config globale)
      await setDoc(doc(db, 'settings', 'modulePermissions'), permissions, {
        merge: false,
      });

      // 2️⃣ Met à jour le cache global
      try {
        window.__modulePermissionsCache = permissions;
      } catch (e) {
        logger.error('Erreur mise à jour window.__modulePermissionsCache:', e);
      }

      // 3️⃣ Met à jour localStorage
      try {
        localStorage.setItem('modulePermissions', JSON.stringify(permissions));
      } catch (e) {
        logger.error('Erreur en écrivant modulePermissions dans localStorage:', e);
      }

      setHasChanges(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);

      logger.log('✅ Permissions modules sauvegardées et appliquées.');
    } catch (error) {
      logger.error('❌ Erreur lors de la sauvegarde des permissions modules:', error);
      setLoadError("Erreur lors de la sauvegarde des permissions. Veuillez réessayer.");
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/40">
            <Shield className="w-6 h-6 text-blue-600 dark:text-blue-300" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Permissions d’accès par module
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Définissez quels rôles peuvent accéder à chaque module de l’application.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetAll}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <X className="w-3 h-3" />
            Réinitialiser tout
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || loading}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors ${
              hasChanges && !loading
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4" />
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Messages de statut */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Chargement des permissions...
        </div>
      )}

      {loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertCircle className="mt-0.5 w-4 h-4 flex-shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {showSuccess && (
        <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200">
          <Check className="mt-0.5 w-4 h-4 flex-shrink-0" />
          <span>Permissions enregistrées et appliquées.</span>
        </div>
      )}

      {/* Tableau des permissions */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Module
              </th>
              {roleIds.map((roleId) => (
                <th
                  key={roleId}
                  className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                >
                  {ROLES[roleId]?.label || roleId}
                </th>
              ))}
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {MODULES.map((module) => {
              const Icon = module.icon;
              return (
                <tr key={module.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 p-1.5 rounded-full bg-gray-100 dark:bg-gray-800">
                        <Icon className="w-4 h-4 text-gray-700 dark:text-gray-200" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {module.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {module.description}
                        </div>
                      </div>
                    </div>
                  </td>

                  {roleIds.map((roleId) => {
                    const hasAccess = getEffectiveAccess(roleId, module.id);
                    const isDefault = !(
                      permissions[roleId] &&
                      Object.prototype.hasOwnProperty.call(permissions[roleId], module.id)
                    );
                    return (
                      <td key={roleId} className="px-4 py-3 text-center align-middle">
                        <button
                          type="button"
                          onClick={() => togglePermission(roleId, module.id)}
                          className={`inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            hasAccess
                              ? 'border-green-500 bg-green-50 text-green-700 dark:border-green-500 dark:bg-green-900/20 dark:text-green-300'
                              : 'border-red-400 bg-red-50 text-red-700 dark:border-red-500 dark:bg-red-900/20 dark:text-red-200'
                          }`}
                          title={
                            hasAccess
                              ? 'Cliquer pour retirer l’accès à ce module pour ce rôle'
                              : 'Cliquer pour autoriser l’accès à ce module pour ce rôle'
                          }
                        >
                          {hasAccess ? (
                            <>
                              <Eye className="w-3 h-3 mr-1" />
                              Activé
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3 mr-1" />
                              Désactivé
                            </>
                          )}
                          {isDefault && (
                            <span className="ml-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              (défaut)
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}

                  <td className="px-4 py-3 text-right align-middle">
                    <button
                      type="button"
                      onClick={() => resetRole}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 dark:border-gray-600 px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <X className="w-3 h-3" />
                      Réinit. rôle
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Alerte de sauvegarde */}
      {hasChanges && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-100">
          <AlertCircle className="mt-0.5 w-4 h-4 flex-shrink-0" />
          <p>
            N&apos;oubliez pas de sauvegarder ! Les changements sont appliqués immédiatement après
            l’enregistrement.
          </p>
        </div>
      )}
    </div>
  );
};

export default ModulePermissionsManager;